import React from "react";
import { NeoButton, NeoDropdown } from "../../../../../components/Common";
import BlocksEditor from "./BlocksEditor";
import StrengthVolumeSummary from "../strength/StrengthVolumeSummary";
import RunningClubSettings from "../running/RunningClubSettings";
import RunningCoachBrief from "../running/RunningCoachBrief";
import RunningCycleSettings from "../running/RunningCycleSettings";
import { calculateRunningWorkoutSummary } from "../../domain/running/calculations";

/**
 * Painel de uma sessão de treino (um dia/aba).
 * Exibe: trigger cards de biblioteca por modalidade, configurações avançadas
 * de corrida (clube, ciclo, coach brief) e o BlocksEditor.
 *
 * @param {Object}   workout                  - Dados da sessão (formik values)
 * @param {number}   index                    - Índice da sessão/aba
 * @param {string}   defaultWorkoutType       - Tipo de treino padrão
 * @param {Array}    workouts                 - Array de todas as sessões (p/ StrengthVolumeSummary)
 * @param {Function} handleChange             - Formik handleChange
 * @param {Function} setFieldValue            - Formik setFieldValue
 * @param {Function} onLibraryChanged         - Callback quando a lib é alterada
 * @param {Object}   runningConfigTab         - Estado da aba de config de corrida (por index)
 * @param {Function} setRunningConfigTab      - Setter do estado de config de corrida
 * @param {Object}   runningCoachBriefOpen    - Estado do coach brief (por index)
 * @param {Function} setRunningCoachBriefOpen - Setter do estado de coach brief
 * @param {Function} onOpenLibrary            - Callback para abrir o Offcanvas da biblioteca
 * @param {Function} onOpenRunningHelp        - Callback para abrir o guia de corrida
 * @param {Function} onSaveRunningSession     - Callback para salvar sessão de corrida na lib
 * @param {Function} onExportRunningWatch     - Callback para exportar treino p/ relógio
 */
const WorkoutSessionPanel = ({
    workout,
    index,
    defaultWorkoutType,
    workouts,
    handleChange,
    setFieldValue,
    onLibraryChanged,
    runningConfigTab,
    setRunningConfigTab,
    runningCoachBriefOpen,
    setRunningCoachBriefOpen,
    onOpenLibrary,
    onOpenRunningHelp,
    onSaveRunningSession,
    onExportRunningWatch,
}) => {
    const currentSessionType = workout.sessionType || workout.workoutType || defaultWorkoutType;
    const isCurrentSwimming = currentSessionType === "swimming";
    const isCurrentStrength = currentSessionType === "strength";
    const isCurrentWod = currentSessionType === "wod";
    const isCurrentRunning = currentSessionType === "running";
    const runningSummary = isCurrentRunning ? calculateRunningWorkoutSummary(workout) : {};

    return (
        <div className="p-1">
            {/* ── Trigger Card: Natação ── */}
            {isCurrentSwimming && (
                <div className="d-flex align-items-center justify-content-between p-3 mb-3 swim-session-library-trigger-card">
                    <div className="d-flex align-items-center gap-2">
                        <span className="workout-modality-icon-wrapper swimming">
                            <i className="mdi mdi-swim" />
                        </span>
                        <div>
                            <h6 className="mb-0 fw-bold text-dark font-size-13">Biblioteca de Sets</h6>
                            <span className="text-muted font-size-11">Utilize sets prontos do sistema ou personalizados na sua sessão</span>
                        </div>
                    </div>
                    <NeoButton
                        type="button"
                        variant="soft-primary"
                        size="sm"
                        icon="mdi-book-open-variant"
                        onClick={() => onOpenLibrary("swimming")}
                    >
                        Abrir Biblioteca
                    </NeoButton>
                </div>
            )}

            {/* ── Trigger Card: Musculação + Volume Summary ── */}
            {isCurrentStrength && (
                <>
                    <div className="d-flex align-items-center justify-content-between p-3 mb-3 strength-session-library-trigger-card">
                        <div className="d-flex align-items-center gap-2">
                            <span className="workout-modality-icon-wrapper strength">
                                <i className="mdi mdi-dumbbell" />
                            </span>
                            <div>
                                <h6 className="mb-0 fw-bold text-dark font-size-13">Biblioteca de Exercícios</h6>
                                <span className="text-muted font-size-11">Utilize exercícios da base oficial ou seus personalizados</span>
                            </div>
                        </div>
                        <NeoButton
                            type="button"
                            variant="soft-warning"
                            size="sm"
                            icon="mdi-book-open-variant"
                            onClick={() => onOpenLibrary("strength")}
                        >
                            Abrir Biblioteca
                        </NeoButton>
                    </div>
                    <StrengthVolumeSummary
                        workout={workout}
                        workouts={workouts}
                    />
                </>
            )}

            {/* ── Trigger Card: WOD ── */}
            {isCurrentWod && (
                <div className="d-flex align-items-center justify-content-between p-3 mb-3 wod-session-library-trigger-card">
                    <div className="d-flex align-items-center gap-2">
                        <span className="workout-modality-icon-wrapper wod">
                            <i className="mdi mdi-lightning-bolt" />
                        </span>
                        <div>
                            <h6 className="mb-0 fw-bold text-dark font-size-13">Biblioteca de WODs</h6>
                            <span className="text-muted font-size-11">Benchmarks, Heroes, Girls, Open e treinos da box</span>
                        </div>
                    </div>
                    <NeoButton
                        type="button"
                        variant="soft-warning"
                        size="sm"
                        icon="mdi-book-open-variant"
                        onClick={() => onOpenLibrary("wod")}
                    >
                        Abrir Biblioteca
                    </NeoButton>
                </div>
            )}

            {/* ── Trigger Card + Config: Corrida ── */}
            {isCurrentRunning && (
                <>
                    <div className="d-flex align-items-center justify-content-between p-3 mb-3 running-session-library-trigger-card">
                        <div className="d-flex align-items-center gap-2">
                            <span className="workout-modality-icon-wrapper running">
                                <i className="mdi mdi-run-fast" />
                            </span>
                            <div>
                                <h6 className="mb-0 fw-bold text-dark font-size-13">Biblioteca de Sessões</h6>
                                <span className="text-muted font-size-11 d-block mb-1">Intervalados, longões, fartleks, subidas e sessões da assessoria</span>
                                {runningSummary.blocksCount > 0 && (
                                    <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                                        <span className="neo-stat-badge">
                                            <i className="mdi mdi-map-marker-distance me-1 text-success" />
                                            Volume: <span className="text-success fw-bold">{runningSummary.distanceKm.toFixed(1).replace(".", ",")} km</span>
                                        </span>
                                        <span className="neo-stat-badge">
                                            <i className="mdi mdi-clock-outline me-1 text-success" />
                                            Duração: <span className="text-success fw-bold">{runningSummary.durationMin > 0 ? `${Math.round(runningSummary.durationMin)} min` : "-"}</span>
                                        </span>
                                        <span className="neo-stat-badge">
                                            <i className="mdi mdi-layers-outline me-1 text-success" />
                                            Blocos: <span className="text-success fw-bold">{runningSummary.blocksCount}</span>
                                        </span>
                                        <span className="neo-stat-badge">
                                            <i className="mdi mdi-format-list-bulleted me-1 text-success" />
                                            Segmentos: <span className="text-success fw-bold">{runningSummary.segmentsCount}</span>
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <NeoButton
                                type="button"
                                variant="soft-success"
                                size="sm"
                                icon="mdi-book-open-variant"
                                onClick={() => onOpenLibrary("running")}
                            >
                                Abrir Biblioteca
                            </NeoButton>
                            <NeoButton
                                type="button"
                                variant="outline-success"
                                size="sm"
                                icon="mdi-help-circle-outline"
                                onClick={onOpenRunningHelp}
                            >
                                Guia
                            </NeoButton>
                            <NeoDropdown
                                label="Mais"
                                icon="mdi-dots-horizontal"
                                variant="secondary"
                                size="sm"
                                menuAlign="end"
                                items={[
                                    {
                                        label: "Modo Professor",
                                        icon: "mdi-clipboard-text-outline text-success",
                                        onClick: () => setRunningCoachBriefOpen(prev => ({
                                            ...prev,
                                            [index]: !prev[index],
                                        }))
                                    },
                                    {
                                        label: "Salvar Sessão",
                                        icon: "mdi-content-save-outline text-success",
                                        onClick: () => onSaveRunningSession(index, workout)
                                    },
                                    {
                                        label: "Exportar Relógio",
                                        icon: "mdi-watch-variant text-success",
                                        onClick: () => onExportRunningWatch(workout)
                                    }
                                ]}
                            />
                        </div>
                    </div>
                    {/* Tab selector for advanced configs */}
                    <div className="d-flex bg-white p-1 rounded border mb-3 justify-content-between align-items-center neo-config-tab-bar">
                        <div className="d-flex gap-1">
                            <NeoButton
                                type="button"
                                size="sm"
                                variant={(runningConfigTab[index] || "none") === "none" ? "soft-success" : "ghost"}
                                icon="mdi-run"
                                onClick={() => setRunningConfigTab(prev => ({ ...prev, [index]: "none" }))}
                            >
                                Treino Individual
                            </NeoButton>

                            <NeoButton
                                type="button"
                                size="sm"
                                variant={runningConfigTab[index] === "club" ? "soft-success" : "ghost"}
                                icon="mdi-account-group"
                                onClick={() => setRunningConfigTab(prev => ({ ...prev, [index]: "club" }))}
                            >
                                Modo Clube
                            </NeoButton>

                            <NeoButton
                                type="button"
                                size="sm"
                                variant={runningConfigTab[index] === "cycle" ? "soft-success" : "ghost"}
                                icon="mdi-calendar-range"
                                onClick={() => setRunningConfigTab(prev => ({ ...prev, [index]: "cycle" }))}
                            >
                                Ciclo e Prova
                            </NeoButton>
                        </div>
                        <span className="text-muted font-size-11 pe-2 d-none d-md-inline-block">Configurações Avançadas</span>
                    </div>

                    {/* Conditional Forms */}
                    {runningConfigTab[index] === "club" && (
                        <RunningClubSettings
                            session={workout}
                            namePrefix={`sessions.${index}`}
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                        />
                    )}

                    {runningConfigTab[index] === "cycle" && (
                        <RunningCycleSettings
                            session={workout}
                            namePrefix={`sessions.${index}`}
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                        />
                    )}

                    {runningCoachBriefOpen[index] && (
                        <RunningCoachBrief workout={workout} />
                    )}
                </>
            )}

            {/* ── Editor de Blocos ── */}
            <BlocksEditor
                blocks={workout.blocks}
                namePrefix={`sessions.${index}.blocks`}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                defaultWorkoutType={currentSessionType}
                runningPaceGroups={(workout.clubSession?.isClubSession ? workout.clubSession?.paceGroups : []) || []}
                onLibraryChanged={onLibraryChanged}
            />
        </div>
    );
};

export default WorkoutSessionPanel;
